import {
  Box,
  Tabs as ChakraTabs,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FaStop } from "react-icons/fa"
import {
  FiActivity,
  FiArrowLeft,
  FiDatabase,
  FiMapPin,
  FiPlay,
  FiSearch,
  FiSettings,
  FiWifi,
  FiWifiOff,
} from "react-icons/fi"

import { ReadersService } from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import {
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
} from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import { InputGroup } from "@/components/ui/input-group"
import { OrganizationContext } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/reader-control")({
  component: ReaderControl,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      readerId: search.readerId as string | undefined,
    }
  },
})

function ReaderControl() {
  const { t } = useTranslation(["readers", "common"])
  const sc = useSemanticColors()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const { readerId } = useSearch({ from: "/_layout/reader-control" })
  const { currentOrganization, organizations } = useContext(OrganizationContext)
  const [activeTab, setActiveTab] = useState("0")
  const [isInventoryRunning, setIsInventoryRunning] = useState(false)
  const [inventoryResults, setInventoryResults] = useState<any[]>([])
  const [confirmClear, setConfirmClear] = useState(false)
  const [tagSearch, setTagSearch] = useState("")
  const [connectionStatus, setConnectionStatus] = useState<
    "idle" | "connected" | "no_response" | "error"
  >("idle")
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch reader data — refetch every 30s to keep mqtt_last_seen current
  const { data: reader, isLoading: readerLoading } = useQuery({
    queryKey: ["reader", readerId],
    queryFn: () => ReadersService.readReader({ id: readerId! }),
    enabled: !!readerId,
    refetchInterval: 30000,
  })

  // Use reader's own org for API calls (works even when "All Organizations" is selected)
  const orgId = reader?.organisation_id || currentOrganization?.id

  const fetchRecentActivity = async () => {
    if (!orgId) return
    const token = localStorage.getItem("access_token")
    const base = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "")
    const url = `${base}/api/v1/organizations/${orgId}/tags/recent-activity?minutes=30&limit=50`
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      const data = await res.json()
      if (Array.isArray(data) && data.length > 0) {
        setInventoryResults(data)
        setActiveTab("3")
      }
    } catch (_) {}
  }

  const startPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    // Fetch immediately, then every 3 seconds
    fetchRecentActivity()
    pollIntervalRef.current = setInterval(fetchRecentActivity, 3000)
  }

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  // Clean up on unmount
  useEffect(() => () => stopPolling(), [])

  // Auto-load recent readings when Live Inventory tab is opened
  useEffect(() => {
    if (activeTab === "3" && currentOrganization) {
      fetchRecentActivity()
    }
  }, [activeTab])

  // Inventory mutation
  const inventoryMutation = useMutation({
    mutationFn: async () => {
      const result = await ReadersService.organizationReaderInventory({
        organisationId: orgId!,
        readerId: readerId!,
        details: true,
        timeout: 30,
      })
      return result
    },
    onSuccess: () => {
      showToast.showSuccessToast(t("readers:control.inventoryStarted"))
      setActiveTab("3")
      setIsInventoryRunning(true)
      startPolling()
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || t("readers:control.inventoryFailed")
      showToast.showErrorToast(`${errDetail}`)
      setIsInventoryRunning(false)
    },
  })

  const stopMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem("access_token")
      const base = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "")
      const url = `${base}/api/v1/organizations/${orgId!}/readers/${readerId}/stop`
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`Stop failed: ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      setIsInventoryRunning(false)
      stopPolling()
      showToast.showSuccessToast(t("readers:control.inventoryStopped"))
    },
    onError: () => {
      setIsInventoryRunning(false)
      stopPolling()
      showToast.showErrorToast(t("readers:control.stopFailed"))
    },
  })

  const checkConnectionMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem("access_token")
      const base = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "")
      const url = `${base}/api/v1/organizations/${orgId!}/readers/${readerId}/version?timeout=10`
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: (data: any) => {
      const responses = data?.responses ?? []
      const hasRealResponse = responses.some(
        (r: any) => r.status !== "ERROR" && !r.error,
      )
      if (hasRealResponse) {
        setConnectionStatus("connected")
        showToast.showSuccessToast(t("readers:control.readerConnected"))
      } else {
        setConnectionStatus("no_response")
        showToast.showErrorToast(t("readers:control.readerNoResponse"))
      }
    },
    onError: () => {
      setConnectionStatus("error")
      showToast.showErrorToast(t("readers:control.readerFailed"))
    },
  })

  const rebootMutation = useMutation({
    mutationFn: () =>
      ReadersService.organizationReaderReboot({
        organisationId: orgId!,
        readerId: readerId!,
      }),
    onSuccess: () =>
      showToast.showSuccessToast(t("readers:control.rebootSent")),
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || t("readers:control.rebootFailed")
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleReadTags5s = async () => {
    setIsInventoryRunning(true)
    setActiveTab("3")
    // Start inventory
    await ReadersService.organizationReaderInventory({
      organisationId: orgId!,
      readerId: readerId!,
      timeout: 30,
    }).catch(() => {})
    showToast.showSuccessToast(t("readers:control.reading5s"))
    startPolling()
    // Stop after 5 seconds
    setTimeout(async () => {
      const token = localStorage.getItem("access_token")
      const base = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "")
      await fetch(
        `${base}/api/v1/organizations/${orgId!}/readers/${readerId}/stop`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        },
      ).catch(() => {})
      setIsInventoryRunning(false)
      stopPolling()
      showToast.showSuccessToast(t("readers:control.read5sComplete"))
    }, 5000)
  }

  const handleSendCommand = (command: string) => {
    if (!reader) return

    if (command === "START_INVENTORY") {
      setIsInventoryRunning(true)
      inventoryMutation.mutate()
    } else if (command === "READ_TAGS") {
      handleReadTags5s()
    } else if (command === "STOP_INVENTORY") {
      stopMutation.mutate()
    } else if (command === "REBOOT") {
      rebootMutation.mutate()
    } else {
      showToast.showSuccessToast(t("readers:control.commandSent", { command }))
    }
  }

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("common:org.noOrg")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  if (!readerId) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("readers:control.noReaderSelected")}
        </Heading>
        <Text color={sc.textMuted}>
          {t("readers:control.selectReaderPrompt")}
        </Text>
      </VisionCard>
    )
  }

  if (readerLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (!reader) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("readers:control.readerNotFound")}
        </Heading>
        <Text color={sc.textMuted}>
          {t("readers:control.readerNotFoundDesc")}
        </Text>
      </VisionCard>
    )
  }

  const MQTT_CONNECTED_THRESHOLD_MS = 3 * 60 * 1000
  const isConnected =
    !!(reader as any)?.mqtt_credentials_active &&
    !!(reader as any)?.mqtt_last_seen &&
    Date.now() - new Date((reader as any).mqtt_last_seen).getTime() <
      MQTT_CONNECTED_THRESHOLD_MS

  const getStateColor = (state: string | undefined) => {
    switch (state) {
      case "INVENTORY":
        return "green"
      case "CONFIGURATION":
        return "blue"
      case "DATADUMP":
        return "purple"
      case "STANDBY":
        return "gray"
      case "ERROR":
        return "red"
      case "MAINTENANCE":
        return "orange"
      default:
        return "gray"
    }
  }

  return (
    <Box>
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        gap={{ base: 3, md: 4 }}
        mb={{ base: 5, md: 8 }}
      >
        <Box flex={1} minW={0}>
          <Heading
            size={{ base: "lg", md: "xl" }}
            color={sc.heading}
            lineClamp={2}
          >
            {t("readers:control.title", { name: reader.name })}
          </Heading>
          <Flex gap={2} mt={1} wrap="wrap" align="center">
            <Badge colorScheme={getStateColor(reader.observed_state)} size="sm">
              {reader.observed_state}
            </Badge>
            <Text color={sc.textMuted} fontSize="sm" wordBreak="break-all">
              {reader.device_model}
              {reader.mac_address ? ` • ${reader.mac_address}` : ""}
            </Text>
          </Flex>
        </Box>
        <Flex gap={2} wrap="wrap" align="center">
          <HStack
            gap={2}
            px={3}
            py={1.5}
            borderRadius="8px"
            bg={
              isConnected
                ? "rgba(72, 187, 120, 0.12)"
                : "rgba(255, 255, 255, 0.04)"
            }
            border="1px solid"
            borderColor={isConnected ? "rgba(72, 187, 120, 0.35)" : sc.border}
          >
            {isConnected ? (
              <FiWifi color="#48BB78" size={15} />
            ) : (
              <FiWifiOff color="#A0AEC0" size={15} />
            )}
            <Text
              fontSize="sm"
              fontWeight="medium"
              color={isConnected ? "green.400" : sc.textMuted}
            >
              {isConnected
                ? t("readers:control.connected")
                : t("readers:control.disconnected")}
            </Text>
          </HStack>
          <VisionButton
            variant="ghost"
            onClick={() => navigate({ to: "/readers" })}
            size="sm"
          >
            <FiArrowLeft />
            {t("common:actions.back")}
          </VisionButton>
        </Flex>
      </Flex>

      <Grid
        templateColumns={{ base: "1fr", lg: "2fr 1fr" }}
        gap={{ base: 4, md: 6 }}
      >
        <Box>
          <ChakraTabs.Root
            value={activeTab}
            onValueChange={(e) => setActiveTab(e.value)}
          >
            <Box overflowX="auto" mb={6} maxW="100%">
              <ChakraTabs.List style={{ minWidth: "max-content" }}>
                <ChakraTabs.Trigger value="0">
                  <FiPlay style={{ marginRight: "8px" }} />
                  {t("readers:control.inventory")}
                </ChakraTabs.Trigger>
                <ChakraTabs.Trigger value="1">
                  <FiSettings style={{ marginRight: "8px" }} />
                  {t("readers:control.configuration")}
                </ChakraTabs.Trigger>
                <ChakraTabs.Trigger value="2">
                  <FiDatabase style={{ marginRight: "8px" }} />
                  {t("readers:control.dataManagement")}
                </ChakraTabs.Trigger>
                <ChakraTabs.Trigger value="3">
                  <FiActivity style={{ marginRight: "8px" }} />
                  {t("readers:control.liveInventory")} (
                  {inventoryResults.length})
                </ChakraTabs.Trigger>
              </ChakraTabs.List>
            </Box>

            <ChakraTabs.Content value="0">
              <VisionCard>
                <Heading size="md" color={sc.heading} mb={6}>
                  {t("readers:control.inventoryOps")}
                </Heading>
                <VStack gap={4} align="stretch">
                  <Box>
                    <Text color={sc.textMuted} fontSize="sm" mb={2}>
                      {t("readers:control.inventoryOpsDesc")}
                    </Text>
                    <Flex gap={3} wrap="wrap">
                      <VisionButton
                        variant="brand"
                        onClick={() => handleSendCommand("START_INVENTORY")}
                        disabled={isInventoryRunning}
                      >
                        <FiPlay /> {t("readers:control.startInventory")}
                      </VisionButton>
                      <VisionButton
                        variant="ghost"
                        onClick={() => handleSendCommand("STOP_INVENTORY")}
                        loading={stopMutation.isPending}
                      >
                        <FaStop /> {t("readers:control.stopInventory")}
                      </VisionButton>
                    </Flex>
                  </Box>

                  <Box>
                    <Text color={sc.textMuted} fontSize="sm" mb={2}>
                      {t("readers:control.inventoryOpsDesc")}
                    </Text>
                    <VisionButton
                      variant="dark"
                      onClick={() => handleSendCommand("READ_TAGS")}
                    >
                      <FiActivity /> {t("readers:control.readTags")}
                    </VisionButton>
                  </Box>
                </VStack>
              </VisionCard>
            </ChakraTabs.Content>

            <ChakraTabs.Content value="1">
              <VisionCard>
                <Heading size="md" color={sc.heading} mb={6}>
                  {t("readers:control.readerConfig")}
                </Heading>
                <VStack gap={4} align="stretch">
                  <Box>
                    <Text color={sc.textMuted} fontSize="sm" mb={2}>
                      {t("readers:control.antennaPower")}
                    </Text>
                    <Flex gap={3} wrap="wrap">
                      <VisionButton
                        variant="dark"
                        onClick={() => handleSendCommand("SET_POWER_LOW")}
                      >
                        {t("readers:control.powerLow")}
                      </VisionButton>
                      <VisionButton
                        variant="dark"
                        onClick={() => handleSendCommand("SET_POWER_MEDIUM")}
                      >
                        {t("readers:control.powerMedium")}
                      </VisionButton>
                      <VisionButton
                        variant="dark"
                        onClick={() => handleSendCommand("SET_POWER_HIGH")}
                      >
                        {t("readers:control.powerHigh")}
                      </VisionButton>
                    </Flex>
                  </Box>

                  <Box>
                    <Text color={sc.textMuted} fontSize="sm" mb={2}>
                      {t("readers:control.stateControl")}
                    </Text>
                    <VStack gap={2} align="stretch">
                      <VisionButton
                        variant="dark"
                        onClick={() => handleSendCommand("SET_STATE_STANDBY")}
                      >
                        {t("readers:control.setStandby")}
                      </VisionButton>
                      <VisionButton
                        variant="dark"
                        onClick={() =>
                          handleSendCommand("SET_STATE_CONFIGURATION")
                        }
                      >
                        {t("readers:control.enterConfig")}
                      </VisionButton>
                    </VStack>
                  </Box>
                </VStack>
              </VisionCard>
            </ChakraTabs.Content>

            <ChakraTabs.Content value="2">
              <VisionCard>
                <Heading size="md" color={sc.heading} mb={6}>
                  {t("readers:control.dataOps")}
                </Heading>
                <VStack gap={4} align="stretch">
                  <Box>
                    <Text color={sc.textMuted} fontSize="sm" mb={2}>
                      {t("readers:control.exportDesc")}
                    </Text>
                    <VisionButton
                      variant="dark"
                      onClick={() => handleSendCommand("EXPORT_DATA")}
                    >
                      <FiDatabase /> {t("readers:control.exportTags")}
                    </VisionButton>
                  </Box>

                  <Box>
                    <Text color={sc.textMuted} fontSize="sm" mb={2}>
                      {t("readers:control.clearDesc")}
                    </Text>
                    <VisionButton
                      variant="ghost"
                      onClick={() => setConfirmClear(true)}
                    >
                      {t("readers:control.clearTags")}
                    </VisionButton>
                  </Box>
                </VStack>
              </VisionCard>
            </ChakraTabs.Content>

            <ChakraTabs.Content value="3">
              <VisionCard>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                  gap={{ base: 3, md: 2 }}
                  mb={6}
                >
                  <Heading size="md" color={sc.heading}>
                    {t("readers:control.liveResults")}
                  </Heading>
                  <Flex gap={2} wrap="wrap" align="center">
                    {isInventoryRunning && (
                      <HStack gap={1}>
                        <Spinner size="xs" color="green.400" />
                        <Text fontSize="xs" color="green.400">
                          {t("readers:control.scanning")}
                        </Text>
                      </HStack>
                    )}
                    <VisionButton
                      variant="brand"
                      size="sm"
                      onClick={() => handleSendCommand("START_INVENTORY")}
                      disabled={isInventoryRunning}
                      loading={inventoryMutation.isPending}
                    >
                      <FiPlay /> {t("readers:control.start")}
                    </VisionButton>
                    <VisionButton
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSendCommand("STOP_INVENTORY")}
                      loading={stopMutation.isPending}
                    >
                      <FaStop /> {t("readers:control.stop")}
                    </VisionButton>
                    <VisionButton
                      variant="brand"
                      size="sm"
                      onClick={fetchRecentActivity}
                    >
                      <FiActivity /> {t("readers:control.refresh")}
                    </VisionButton>
                  </Flex>
                </Flex>

                {inventoryMutation.isPending && (
                  <Flex justify="center" align="center" py={8}>
                    <Spinner size="lg" color={sc.spinnerColor} />
                    <Text color={sc.heading} ml={3}>
                      {t("readers:control.starting")}
                    </Text>
                  </Flex>
                )}

                {!inventoryMutation.isPending &&
                  inventoryResults.length === 0 && (
                    <Flex
                      direction="column"
                      align="center"
                      justify="center"
                      py={8}
                    >
                      <IconBox variant="glass" size="lg" mb={4}>
                        <FiActivity fontSize="24px" color={sc.iconColor} />
                      </IconBox>
                      <Text color={sc.textMuted} textAlign="center">
                        {t("readers:control.noTags")}
                      </Text>
                    </Flex>
                  )}

                {inventoryResults.length > 0 && (
                  <Box mb={4}>
                    <InputGroup startElement={<FiSearch color="gray" />}>
                      <VisionInput
                        placeholder={t("readers:control.searchTags")}
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                      />
                    </InputGroup>
                    {tagSearch && (
                      <Text fontSize="xs" color={sc.textSubtle} mt={1}>
                        {t("readers:control.filteredTags", {
                          filtered: inventoryResults.filter((tag) => {
                            const q = tagSearch.toLowerCase()
                            return (
                              tag.epcid?.toLowerCase().includes(q) ||
                              tag.tag_name?.toLowerCase().includes(q)
                            )
                          }).length,
                          total: inventoryResults.length,
                        })}
                      </Text>
                    )}
                  </Box>
                )}

                {inventoryResults.length > 0 && (
                  <VStack gap={2} align="stretch" maxH="400px" overflowY="auto">
                    {inventoryResults
                      .filter((tag: any) => {
                        if (!tagSearch) return true
                        const q = tagSearch.toLowerCase()
                        return (
                          tag.epcid?.toLowerCase().includes(q) ||
                          tag.tag_name?.toLowerCase().includes(q)
                        )
                      })
                      .map((tag: any, index: number) => {
                        // "New" if the tag row was created at (or right
                        // around) this scan's timestamp — i.e. this event
                        // is what caused auto-discovery. Otherwise the tag
                        // existed before this scan ("Known").
                        const isNewlyDiscovered =
                          tag.tag_created_at && tag.timestamp
                            ? Math.abs(
                                new Date(tag.timestamp).getTime() -
                                  new Date(tag.tag_created_at).getTime(),
                              ) < 30_000
                            : false
                        return (
                          <Box
                            key={`${tag.tag_id}-${tag.timestamp}-${index}`}
                            p={3}
                            borderRadius="8px"
                            bg={sc.eventBg}
                            backdropFilter="blur(20px)"
                            border="1px solid"
                            borderColor={sc.border}
                          >
                            <HStack justify="space-between">
                              <VStack align="start" gap={1}>
                                <HStack gap={2}>
                                  <Text
                                    fontSize="sm"
                                    color={sc.heading}
                                    fontFamily="mono"
                                  >
                                    EPC: {tag.epcid}
                                  </Text>
                                  <Badge
                                    colorScheme={
                                      isNewlyDiscovered ? "green" : "gray"
                                    }
                                    size="sm"
                                  >
                                    {isNewlyDiscovered
                                      ? t("readers:control.newTag")
                                      : t("readers:control.knownTag")}
                                  </Badge>
                                </HStack>
                                <Text fontSize="xs" color={sc.textMuted}>
                                  {tag.tag_name}
                                </Text>
                              </VStack>
                              <VStack align="end" gap={1}>
                                {tag.rssi !== undefined &&
                                  tag.rssi !== null && (
                                    <Badge colorScheme="blue" size="sm">
                                      {tag.rssi} dBm
                                    </Badge>
                                  )}
                                <Text fontSize="xs" color={sc.textSubtle}>
                                  {tag.timestamp
                                    ? new Date(
                                        tag.timestamp,
                                      ).toLocaleTimeString()
                                    : ""}
                                </Text>
                              </VStack>
                            </HStack>
                          </Box>
                        )
                      })}
                  </VStack>
                )}
              </VisionCard>
            </ChakraTabs.Content>
          </ChakraTabs.Root>
        </Box>

        <VStack gap={6}>
          <VisionCard>
            <Heading size="md" color={sc.heading} mb={4}>
              {t("readers:control.readerInfo")}
            </Heading>
            <VStack align="stretch" gap={3}>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  {t("readers:control.location")}
                </Text>
                <HStack gap={2} mt={1}>
                  <FiMapPin fontSize="14px" color={sc.textMuted} />
                  <Text color={sc.heading} fontSize="sm">
                    {reader.lat != null && reader.lon != null
                      ? `${reader.lat.toFixed(6)}, ${reader.lon.toFixed(6)}`
                      : "—"}
                  </Text>
                </HStack>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  {t("readers:form.role")}
                </Text>
                <Text color={sc.heading} fontSize="sm" mt={1}>
                  {reader.role}
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  {t("readers:form.model")}
                </Text>
                <Text color={sc.heading} fontSize="sm" mt={1}>
                  {reader.device_model}
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  {t("common:filter.organisation")}
                </Text>
                <Text color={sc.heading} fontSize="sm" mt={1}>
                  {organizations.find((o) => o.id === reader.organisation_id)
                    ?.name || "—"}
                </Text>
              </Box>
            </VStack>
          </VisionCard>

          <VisionCard>
            <Heading size="md" color={sc.heading} mb={4}>
              {t("readers:control.quickActions")}
            </Heading>
            <VStack gap={2} align="stretch">
              {!isInventoryRunning ? (
                <VisionButton
                  variant="brand"
                  onClick={() => inventoryMutation.mutate()}
                  loading={inventoryMutation.isPending}
                  w="100%"
                >
                  <FiPlay /> {t("readers:control.startInventory")}
                </VisionButton>
              ) : (
                <VisionButton
                  variant="dark"
                  onClick={() => stopMutation.mutate()}
                  loading={stopMutation.isPending}
                  w="100%"
                  style={{ borderColor: "rgba(255,100,100,0.4)" }}
                >
                  <FaStop /> {t("readers:control.stopInventory")}
                </VisionButton>
              )}
              <VisionButton
                variant="dark"
                onClick={() => handleSendCommand("REBOOT")}
                w="100%"
              >
                {t("readers:control.reboot")}
              </VisionButton>
              <VisionButton
                variant="dark"
                onClick={() => {
                  setConnectionStatus("idle")
                  checkConnectionMutation.mutate()
                }}
                loading={checkConnectionMutation.isPending}
                w="100%"
              >
                <FiWifi />{" "}
                {checkConnectionMutation.isPending
                  ? t("readers:control.checking")
                  : t("readers:control.checkConnection")}
              </VisionButton>
              {connectionStatus !== "idle" && (
                <Text
                  fontSize="xs"
                  color={
                    connectionStatus === "connected" ? "green.400" : "red.400"
                  }
                  textAlign="center"
                >
                  {connectionStatus === "connected"
                    ? t("readers:control.readerResponding")
                    : connectionStatus === "no_response"
                      ? t("readers:control.readerNoResponse")
                      : t("readers:control.readerFailed")}
                </Text>
              )}
              <VisionButton
                variant="dark"
                onClick={() =>
                  navigate({
                    to: "/reader-form",
                    search: { readerId: reader.id },
                  })
                }
                w="100%"
              >
                <FiSettings /> {t("readers:control.editSettings")}
              </VisionButton>
            </VStack>
          </VisionCard>
        </VStack>
      </Grid>

      <ConfirmDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={() => handleSendCommand("CLEAR_DATA")}
        title={t("readers:control.clearTags")}
        message={t("readers:control.confirmClearData")}
      />
    </Box>
  )
}
